import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { readConsent, writeConsent, buildRecord } from './consent';
import { OPTIONAL_CATEGORIES } from './privacyConfig';

const ConsentContext = createContext(null);

/**
 * Holds the visitor's privacy choices for the whole app.
 *   has('security')  → true only after an explicit yes for that category
 *   decided          → the visitor has answered (any way) and the record is current
 * Nothing optional loads until has(category) is true.
 */
export const ConsentProvider = ({ children, initial }) => {
  const [record, setRecord] = useState(() => (initial !== undefined ? initial : null));
  const [loaded, setLoaded] = useState(initial !== undefined);
  const [prefsOpen, setPrefsOpen] = useState(false);

  // Read storage after mount, so prerendered HTML and the first client render
  // agree (react-snap snapshots the page with no stored choices).
  useEffect(() => {
    if (initial !== undefined) return;
    setRecord(readConsent());
    setLoaded(true);
  }, [initial]);

  const save = useCallback((categories, method) => {
    setRecord(writeConsent(buildRecord(categories, method)));
    setPrefsOpen(false);
  }, []);

  const value = useMemo(() => ({
    loaded,
    record,
    decided: Boolean(record),
    has: (category) => category === 'necessary' || Boolean(record && record.categories[category]),
    acceptAll: () => save(OPTIONAL_CATEGORIES.reduce((a, k) => ({ ...a, [k]: true }), {}), 'accept-all'),
    rejectAll: () => save({}, 'reject-all'),
    saveChoices: (categories) => save(categories, 'custom'),
    // Agreeing inside a form notice adds that one category to whatever else was chosen.
    grant: (category) => save({ ...(record ? record.categories : {}), [category]: true }, `inline:${category}`),
    prefsOpen,
    openPreferences: () => setPrefsOpen(true),
    closePreferences: () => setPrefsOpen(false),
  }), [loaded, record, prefsOpen, save]);

  return <ConsentContext.Provider value={value}>{children}</ConsentContext.Provider>;
};

export const useConsent = () => {
  const ctx = useContext(ConsentContext);
  if (!ctx) throw new Error('useConsent must be used inside <ConsentProvider>');
  return ctx;
};
